import { Link } from "react-router-dom";
import { ShieldCheck, KeyRound, RefreshCw, Fingerprint } from "lucide-react";

const FEATURES = [
  { icon: Fingerprint, title: "One identity", text: "A single Talanta ID across Sport, Digital, Soko, Education, Gaming and Broadcast." },
  { icon: KeyRound, title: "SMS-verified", text: "Sign in securely with your Kenyan mobile number — no passwords to forget." },
  { icon: RefreshCw, title: "Always connected", text: "Short-lived tokens with silent refresh keep your session safe and seamless." },
  { icon: ShieldCheck, title: "Built for SSO", text: "An OIDC-ready issuer, so partners like Talanta Sport can trust your ID." },
];

export const OneIdSection = ({ onLogin, onSignin }) => {
  return (
    <section id="one-id" className="relative overflow-hidden bg-talanta-green text-white py-20 sm:py-28">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #E5A93C 0, transparent 40%), radial-gradient(circle at 80% 70%, #B85A32 0, transparent 40%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-gold">One Talanta ID</span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight">
              One account. The entire talent economy.
            </h2>
            <p className="mt-4 text-white/70">
              Create your One Talanta ID once and carry it everywhere across Talanta Hela. Verified by SMS, secured with
              rotating tokens, and ready to power single sign-on with national partners.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                data-testid="oneid-cta"
                onClick={onLogin}
                className="rounded-full bg-talanta-red hover:bg-talanta-red/90 px-7 py-3.5 font-semibold shadow-xl shadow-black/30 transition-all hover:scale-[1.03]"
              >
                Create your One Talanta ID
              </button>
              <button data-testid="oneid-signin" onClick={onSignin} className="font-semibold text-talanta-gold hover:underline underline-offset-4">
                I already have an ID — sign in
              </button>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl bg-white/5 border border-white/10 p-5 backdrop-blur-sm hover:bg-white/10 transition-colors">
                  <div className="h-11 w-11 rounded-xl bg-talanta-gold/15 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-talanta-gold" />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-lg">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-white/60">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-talanta-hover text-white/70">
      <div className="kenya-stripe h-1" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row justify-between gap-6">
          <div>
            <img src="/logo-knockout.png" alt="Talanta Hela — monetizing talents" className="h-12 w-auto" />
            <p className="mt-3 text-sm max-w-xs">Monetizing talent, empowering Kenya — one national talent economy.</p>
          </div>
          <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
            <a href="/#about" className="hover:text-talanta-gold">About</a>
            <a href="/#pillars" className="hover:text-talanta-gold">Pillars</a>
            <a href="/#tournaments" className="hover:text-talanta-gold">Tournaments</a>
            <a href="/#leaderboard" className="hover:text-talanta-gold">Leaderboard</a>
            <a href="/#one-id" className="hover:text-talanta-gold">One Talanta ID</a>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-talanta-gold">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-talanta-gold">Terms of Use</Link>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2 text-xs">
          <span>© {new Date().getFullYear()} Talanta Hela. All rights reserved. · Personal data handled under the Kenya Data Protection Act, 2019.</span>
          <span>talentahela.com · Nairobi, Kenya 🇰🇪</span>
        </div>
      </div>
    </footer>
  );
};
